/**
 * PortfolioCommandCenter
 *
 * Composant principal d'affichage du "Portfolio Command Center".
 *
 * 🎯 Objectif :
 *   - Afficher en <5s tous les KPI clés du portefeuille : valeur, risque, structure, interprétation, action.
 *   - Fournir une UI synthétique, interprétée, actionnable, avec signal visuel et CTA.
 *
 * 📦 Props attendues :
 *   - data: {
 *       totalValue: number, // Valeur totale du portefeuille (USD)
 *       change24h: number, // Variation absolue 24h (USD)
 *       change24hPercent: number, // Variation relative 24h (%)
 *       btcDominance: number, // Dominance BTC (0-1)
 *       assetCount: number, // Nombre d'actifs uniques
 *       exchangeCount: number, // Nombre de plateformes connectées
 *       stablecoinExposure: number, // Exposition stablecoins (0-1)
 *       portfolioScore: number, // Score global (0-1)
 *       marketRegime: string, // Régime de marché (ex: BTC_DOMINANCE)
 *       portfolioAlignment: number, // Alignement avec le marché (0-1)
 *       lastSync: string, // Date ISO de dernière synchronisation
 *     }
 *
 * ⚙️ Logique métier intégrée :
 *   - Signal principal si btcDominance > 0.8
 *   - Label diversification faible si assetCount <= 2
 *   - Score: <0.5 = Faible, <0.7 = Moyen, sinon Bon
 *   - Alignement: >0.7 = Aligné, sinon Désaligné
 *   - Couleur de fond dynamique selon le score (rouge, orange, vert)
 *
 * 🧩 UI :
 *   - Card MUI, KPI principaux, Alert, barre de dominance, chips, score, alignement, date sync, CTA
 *   - Labels FR, design premium, responsive
 *
 * 🔗 Intégration :
 *   - À utiliser dans GlobalPortfolioHeader ou toute page dashboard
 *   - Passer les données du backend ou du store via la prop "data"
 *
 * Auteur : GitHub Copilot, 2026
 */
import React, { useEffect, useState } from 'react';
import { Card, Box, Typography, Alert, LinearProgress, Stack, Chip, Button, Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatSyncDate } from '../../../utils/syncDate';

interface PortfolioCommandCenterProps {
  data: {
    totalValue: number;
    change24h: number;
    change24hPercent: number;
    btcDominance: number;
    assetCount: number;
    exchangeCount: number;
    stablecoinExposure: number;
    portfolioScore: number;
    marketRegime: string;
    portfolioAlignment: number;
    lastSync: string;
  };
}


const getScoreLabel = (score: number) => {
  if (score < 0.5) return 'Faible';
  if (score < 0.7) return 'Moyen';
  return 'Bon';
};

const getScoreColor = (score: number) => {
  if (score < 0.5) return 'error';
  if (score < 0.7) return 'warning';
  return 'success';
};

const PortfolioCommandCenter: React.FC<PortfolioCommandCenterProps> = ({ data }) => {
  // --- Risk Engine integration ---
  const [riskFlag, setRiskFlag] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [riskDetails, setRiskDetails] = useState<any>(null);

  useEffect(() => {
    console.log('[PortfolioCommandCenter] Chargement du risk engine...');
    fetch('/api/portfolio/riskEngine')
      .then(res => res.json())
      .then(risk => {
        console.log('[PortfolioCommandCenter] Risk engine response:', risk);
        if (risk && risk.riskFlags && risk.riskFlags.length > 0) {
          setRiskFlag(risk.riskFlags[0]);
          setRiskDetails(risk);
          console.log('[PortfolioCommandCenter] Risk flag détecté:', risk.riskFlags[0]);
        } else {
          console.log('[PortfolioCommandCenter] Aucun risk flag détecté');
        }
      })
      .catch(err => {
        console.error('[PortfolioCommandCenter] Erreur lors du chargement du risk engine:', err);
      });
  }, []);
  // SIGNAL PRINCIPAL (BTC dominance)
  let signal: null | {
    severity: 'warning' | 'error';
    label: string;
    implication: string;
    action: string;
  } = null;
  if (typeof data.btcDominance === 'number' && data.btcDominance > 0.8) {
    signal = {
      severity: 'warning',
      label: 'Portefeuille fortement concentré en BTC',
      implication: 'Risque élevé en cas de rotation vers les altcoins',
      action: 'Réduire l’exposition BTC',
    };
  }

  // Diversification
  let diversification = null;
  if (typeof data.assetCount === 'number' && data.assetCount <= 2) {
    diversification = {
      label: 'Diversification faible',
      severity: 'warning',
    };
  }

  // Score
  const scoreLabel = typeof data.portfolioScore === 'number' ? getScoreLabel(data.portfolioScore) : '--';
  const scoreColor = typeof data.portfolioScore === 'number' ? getScoreColor(data.portfolioScore) : 'default';

  // Alignement marché
  const alignmentLabel = typeof data.portfolioAlignment === 'number' && data.portfolioAlignment > 0.7
    ? 'Aligné avec le régime de marché'
    : 'Désaligné avec le régime de marché';

  // Regime
  const regimeLabel = data.marketRegime === 'BTC_DOMINANCE' ? 'BTC Dominance' : (data.marketRegime || '--');

  // Date formatée
  const formattedSync = formatSyncDate(data.lastSync, '--');

  // CTA dynamique (bonus extension future)
  let ctaLabel = signal?.action || 'Améliorer la diversification';
  if (typeof data.stablecoinExposure === 'number' && data.stablecoinExposure < 0.1) {
    ctaLabel = 'Ajouter des stablecoins';
  }
  if (typeof data.btcDominance === 'number' && data.btcDominance > 0.8 && data.marketRegime !== 'BTC_DOMINANCE') {
    ctaLabel = 'Diversifier vers altcoins';
  }

  return (
    <>
    <Card sx={{ p: 3, borderRadius: 4, background: '#fff' }}>
      {/* NIVEAU 1 — VALEUR (toujours neutre) */}
      <Box>
        <Typography variant="h3" fontWeight={600}>
          {typeof data.totalValue === 'number'
            ? data.totalValue.toLocaleString('fr-FR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
            : '--'}
        </Typography>
        <Typography color="text.secondary">
          {typeof data.change24h === 'number'
            ? `${data.change24h >= 0 ? '+' : ''}${data.change24h.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}`
            : '--'}
          {' ('}
          {typeof data.change24hPercent === 'number'
            ? `${data.change24hPercent >= 0 ? '+' : ''}${data.change24hPercent.toFixed(2)}`
            : '--'}
          {'%) aujourd’hui'}
        </Typography>
      </Box>

      {/* NIVEAU 2 — SIGNAL + IMPLICATION */}
      {signal && riskFlag && (
        <Alert severity={signal.severity} sx={{ mt: 2 }}>
          <strong>{riskFlag.title || signal.label}</strong>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {riskFlag.description || signal.implication}
          </Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => {
            console.log('[PortfolioCommandCenter] Ouverture du Drawer détail risque');
            setDrawerOpen(true);
          }}>
            Voir pourquoi
          </Button>
        </Alert>
      )}

      {/* NIVEAU 3 — EXPOSITION (VISUEL + INTERPRÉTATION) */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2">
          Dominance BTC : {typeof data.btcDominance === 'number' ? (data.btcDominance * 100).toFixed(0) : '--'}%
          {typeof data.btcDominance === 'number' && data.btcDominance > 0.8 ? '🔴 (Très élevée)' : ''}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={typeof data.btcDominance === 'number' ? data.btcDominance * 100 : 0}
          sx={{ height: 8, borderRadius: 4, mt: 1 }}
        />
      </Box>

      {/* NIVEAU 4 — STRUCTURE PORTFOLIO */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {diversification && (
          <Chip
            label={`${typeof data.assetCount === 'number' ? data.assetCount : '--'} actifs • ${diversification.label}`}
            color={diversification.severity as 'warning'}
          />
        )}
        <Chip
          label={`${typeof data.exchangeCount === 'number' ? data.exchangeCount : '--'} plateformes connectées`}
          variant="outlined"
        />
      </Stack>

      {/* NIVEAU 5 — SCORE + MARCHÉ */}
      <Box sx={{ mt: 3 }}>
        <Typography color={scoreColor} fontWeight={600}>
          Score du portefeuille : {typeof data.portfolioScore === 'number' ? (data.portfolioScore * 100).toFixed(0) : '--'} / 100 ({scoreLabel})
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {alignmentLabel} : {regimeLabel}
        </Typography>
      </Box>

      {/* NIVEAU 6 — META (DISCRET) */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
        Dernière synchronisation : {formattedSync}
      </Typography>

      {/* NIVEAU 7 — CTA INTELLIGENT */}
      <Button variant="contained" sx={{ mt: 2 }}>
        {ctaLabel}
      </Button>
    </Card>

    {/* Drawer pour détail du risk flag */}
    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      <Box sx={{ width: 350, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Détail du risque</Typography>
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>
        {riskFlag && (
          <>
            <Typography variant="subtitle1" fontWeight={600}>{riskFlag.title}</Typography>
            <Typography color="text.secondary" sx={{ mb: 1 }}>{riskFlag.description}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}><b>Niveau :</b> {riskFlag.level}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}><b>Impact :</b> {riskFlag.impact}</Typography>
          </>
        )}
        {riskDetails && riskDetails.insights && riskDetails.insights.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Insight IA</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>{riskDetails.insights[0].context}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}><b>Implication :</b> {riskDetails.insights[0].implication}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}><b>Scénario :</b> {riskDetails.insights[0].scenario}</Typography>
          </Box>
        )}
        {riskDetails && riskDetails.recommendations && riskDetails.recommendations.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Recommandation</Typography>
            <Typography variant="body2">{riskDetails.recommendations[0].action} — {riskDetails.recommendations[0].reason}</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
    </>
  );
};

export default PortfolioCommandCenter;
